import dispatcher from "../dispatcher"
import * as api from "../api"
import * as utils from "../helpers/utils"
import {
    EOTEmployeeType, IDeviceReportParameters, IReportResult, IEmployeesReportParameters,
    IJobReportParameters, ITimecardAdhocExportRequest, IAccountManagerSearchReportParameters,
    INewCustomReportParameters,
    IPunchExceptionFilterDto,
    EExceptionType,
    ITimeoffFilter,
    IUserFilter
} from "../models"
import AppStateStore from "../stores/AppStateStore"
import strings from "../localization/strings"

export function clearAsyncSessionId() {
    setTimeout(() => dispatcher.dispatch({ type: "UPDATE_ASYNC_SESSION_ID", data: null }), 0)
}

export async function downloadInOutReport(accountId, levelIdFilter, payGroupIds: string[], userFilter: IUserFilter, referenceDate, filterByDirectReport, filterByDefaultHierarchy, filterByPunchHierarchy, filterByException, filterExceptions: EExceptionType[], cb = null) {
    const asyncSessionId = utils.guid()
    setTimeout(() =>
        dispatcher.dispatch({
            type: "UPDATE_ASYNC_SESSION_ID",
            data: asyncSessionId
        })
        , 0)
    const res = await api.getInOutReportDownload(accountId, levelIdFilter, payGroupIds, userFilter, referenceDate, filterByDirectReport, filterByDefaultHierarchy, filterByPunchHierarchy, filterByException, filterExceptions, asyncSessionId)
    clearAsyncSessionId()
    handleDownload(res.data)
}

export async function downloadEstimatedTimeReport(accountId, levelIdFilter, payGroupIds: string[], userFilter: IUserFilter, referenceDate, filterByDirectReport, filterByDefaultHierarchy, filterByPunchHierarchy, filterByException, filterExceptions: EExceptionType[], reportType) {
    const asyncSessionId = utils.guid()
    setTimeout(() =>
        dispatcher.dispatch({
            type: "UPDATE_ASYNC_SESSION_ID",
            data: asyncSessionId
        })
        , 0)
    const res = await api.getEstimatedTimeReportDownload(accountId, levelIdFilter, payGroupIds, userFilter, referenceDate, filterByDirectReport, filterByDefaultHierarchy, filterByPunchHierarchy, filterByException, filterExceptions, reportType, asyncSessionId)
    clearAsyncSessionId()
    handleDownload(res.data)
}

export function downloadHierarchyPunchReport(accountId, hierarchyId, startDate, endDate, restrictDate) {
    const asyncSessionId = utils.guid()
    setTimeout(() =>
        dispatcher.dispatch({
            type: "UPDATE_ASYNC_SESSION_ID",
            data: asyncSessionId
        })
        , 0)
    api.getHierarchyPunchReportDownload(accountId, hierarchyId, startDate, endDate, restrictDate, asyncSessionId).then(res => {
        clearAsyncSessionId()
        window.location = res.data
    })
}

export async function downloadTimecardAuditReport(accountId, levelIdFilter, payGroupIds: string[], userFilter: IUserFilter, referenceDate, filterByDefaultHierarchy, filterByDirectReport, filterByPunchHierarchy, filterByException, filterExceptions: EExceptionType[], includeAllAudits, endDate) {
    const asyncSessionId = utils.guid()
    setTimeout(() =>
        dispatcher.dispatch({
            type: "UPDATE_ASYNC_SESSION_ID",
            data: asyncSessionId
        })
        , 0)
    const res = await api.getTimecardAuditReportDownload(accountId, levelIdFilter, payGroupIds, userFilter, referenceDate, filterByDefaultHierarchy, filterByDirectReport, filterByPunchHierarchy, filterByException, filterExceptions, includeAllAudits, asyncSessionId, endDate)
    clearAsyncSessionId()
    handleDownload(res.data)
}

export function downloadScheduleReport(scheduleId) {
    api.getScheduleReport(scheduleId).then(res => {
        window.location = res.data
    })
}

export async function downloadAdhocReport(accountId: string, levelIdFilter: string, filterByDefaultHierarchy: boolean, filterByDirectReport: boolean, filterByPunchHierarchy: boolean, filterByException: boolean, filterExceptions: EExceptionType[], request: ITimecardAdhocExportRequest, userFilter: IUserFilter) {
    const res = await api.getAdhocReport(accountId, levelIdFilter, filterByDefaultHierarchy, filterByDirectReport, filterByPunchHierarchy, filterByException, filterExceptions, request, userFilter)
    handleDownload(res.data)
}

export async function saveAdhocReport(accountId: string, request: ITimecardAdhocExportRequest) {
    const res = await api.addAdhocReport(accountId, request)
    return res
}

export async function fetchAdhocReports(accountId: string) {
    const res = await api.fetchAdhocReports(accountId)
    return res
}

export async function deleteAdhocReport(accountId: string, reportId: string) {
    const res = await api.deleteAdhocReport(accountId, reportId)
    return res
}
export async function updateAdhocReport(accountId: string, request: ITimecardAdhocExportRequest) {
    const res = await api.updateAdhocReport(accountId, request)
    return res
}

export async function downloadPunchDetailReport(accountId: string, startDate: string, endDate: string, levelIdFilter: string, payGroupIds: string[], userFilter: IUserFilter, filterByDefaultHierarchy: boolean, filterByDirectReport: boolean, filterByPunchHierarchy: boolean, filterByException: boolean, filterExceptions: EExceptionType[], includePunchPhoto: boolean) {
    if (includePunchPhoto) {
        const asyncSessionId = utils.guid()
        setTimeout(() =>
            dispatcher.dispatch({
                type: "UPDATE_ASYNC_SESSION_ID",
                data: asyncSessionId
            })
            , 0)
        await api.getPunchDetailReportWithPhoto(accountId, startDate, endDate, levelIdFilter, payGroupIds, userFilter, filterByDefaultHierarchy, filterByDirectReport, filterByPunchHierarchy, filterByException, filterExceptions, asyncSessionId)
    }
    else {
        const res = await api.getPunchDetailReport(accountId, startDate, endDate, levelIdFilter, payGroupIds, userFilter, filterByDefaultHierarchy, filterByDirectReport, filterByPunchHierarchy, filterByException, filterExceptions)
        handleDownload(res.data)
    }
}

export async function downloadPunchQuestionResponseReport(accountId: string, startDate: string, endDate: string, levelIdFilter: string, payGroupIds: string[], userFilter: IUserFilter, filterByDefaultHierarchy: boolean, filterByDirectReport: boolean, filterByPunchHierarchy: boolean, filterByException: boolean, filterExceptions: EExceptionType[]) {
    const res = await api.getPunchQuestionResponseReport(accountId, startDate, endDate, levelIdFilter, payGroupIds, userFilter, filterByDefaultHierarchy, filterByDirectReport, filterByPunchHierarchy, filterByException, filterExceptions)
    handleDownload(res.data)
}

export async function downloadTimecardSummaryReport(accountId: string, referenceDate: string, levelIdFilter: string, payGroupIds: string[], userFilter: IUserFilter, filterByDefaultHierarchy: boolean, filterByDirectReport: boolean, filterByPunchHierarchy: boolean, filterByException: boolean, filterExceptions: EExceptionType[]) {
    const res = await api.getTimecardSummaryReport(accountId, referenceDate, levelIdFilter, payGroupIds, userFilter, filterByDefaultHierarchy, filterByDirectReport, filterByPunchHierarchy, filterByException, filterExceptions)
    handleDownload(res.data)
}

/**
 * Fetches the biometric & location exceptions report data, 
 * then triggers a file download in the browser.
 */
export async function downloadBiometricLocationExceptionsReport(
    accountId: string,
    startDate: string,
    endDate: string,
    levelIdFilter: string,
    payGroupIds: string[],
    userFilter: IUserFilter,
    filterByDefaultHierarchy: boolean,
    filterByDirectReport: boolean,
    filterByPunchHierarchy: boolean,
    filterByException: boolean,
    filterExceptions: EExceptionType[]
) {
    const res = await api.getBiometricLocationExceptionsReport(
        accountId,
        startDate,
        endDate,
        levelIdFilter,
        payGroupIds,
        userFilter,
        filterByDefaultHierarchy,
        filterByDirectReport,
        filterByPunchHierarchy,
        filterByException,
        filterExceptions
    )
    handleDownload(res.data)
}

export function downloadClientShiftsReport(clientId, startDate, endDate) {
    api.getClientShiftsReport(clientId, startDate, endDate).then(res => {
        window.location = res.data
    })
}

export async function downloadJobTimecardAdhocReport(accountId: string, jobId: string, request: ITimecardAdhocExportRequest, userFilter: IUserFilter) {
    const res = await api.getJobTimecardAdhocReport(accountId, jobId, request, userFilter)
    handleDownload(res.data)
}

export async function downloadJobTimecardPunchDetailReport(accountId: string, jobId: string, startDate: string, endDate: string, payGroupIds: string[], includePunchPhoto: boolean, userFilter: IUserFilter) {
    const asyncSessionId = utils.guid()
    setTimeout(() =>
        dispatcher.dispatch({
            type: "UPDATE_ASYNC_SESSION_ID",
            data: asyncSessionId
        })
        , 0)
    const res = await api.getJobTimecardPunchDetailReport(accountId, jobId, startDate, endDate, payGroupIds, userFilter, includePunchPhoto, asyncSessionId)
    clearAsyncSessionId()
    handleDownload(res.data)
}

export async function downloadJobTimecardPunchQuestionResponseReport(accountId: string, jobId: string, startDate: string, endDate: string, payGroupIds: string[], userFilter: IUserFilter) {
    const res = await api.getJobTimecardPunchQuestionResponseReport(accountId, jobId, startDate, endDate, payGroupIds, userFilter)
    handleDownload(res.data)
}

export async function downloadJobTimecardSummaryReport(accountId: string, jobId: string, referenceDate: string, payGroupIds: string[], userFilter: IUserFilter) {
    const res = await api.getJobTimecardSummaryReport(accountId, jobId, referenceDate, payGroupIds, userFilter)
    handleDownload(res.data)
}

export async function downloadJobTimecardAuditReport(accountId, jobId, payGroupIds: string[], userFilter: IUserFilter, referenceDate, includeAllAudits, endDate) {
    const asyncSessionId = utils.guid()
    setTimeout(() =>
        dispatcher.dispatch({
            type: "UPDATE_ASYNC_SESSION_ID",
            data: asyncSessionId
        })
        , 0)
    const res = await api.getJobTimecardAuditReportDownload(accountId, jobId, payGroupIds, userFilter, referenceDate, includeAllAudits, asyncSessionId, endDate)
    clearAsyncSessionId()
    handleDownload(res.data)
}

export function downloadUsersData(accountId, employeeReportParams: IEmployeesReportParameters) {
    AppStateStore.setProgressSnackbarContent("loading")
    api.getUsersDataDownload(accountId, employeeReportParams).then(res => {
        window.location = res.data
        AppStateStore.setProgressSnackbarContent(strings.Exported_Successfully)
    })
}

export function downloadJobsData(accountId, jobReportParams: IJobReportParameters) {
    AppStateStore.setProgressSnackbarContent("loading")
    api.getJobsDataDownload(accountId, jobReportParams).then(res => {
        window.location = res.data
        AppStateStore.setProgressSnackbarContent(strings.Exported_Successfully)
    })
}

export function downloadDevicesData(accountId, deviceReportParams: IDeviceReportParameters) {
    AppStateStore.setProgressSnackbarContent("loading")
    api.getDevicesDataDownload(accountId, deviceReportParams).then(res => {
        window.location = res.data
        AppStateStore.setProgressSnackbarContent(strings.Exported_Successfully)
    })
}

export function downloadLeaveRequestsData(accountId, timeoffFilter: ITimeoffFilter, page, limit) {
    AppStateStore.setProgressSnackbarContent("loading")
    api.getLeaveRequestsDataDownload(accountId, timeoffFilter, page, limit).then(res => {
        window.location = res.data
        AppStateStore.setProgressSnackbarContent(strings.Exported_Successfully)
    })
}

export function downloadUserLeaveRequestsData(userId, startDate, endDate, search, timeoffStatusList) {
    AppStateStore.setProgressSnackbarContent("loading")
    api.getUserLeaveRequestsDataDownload(userId, startDate, endDate, search, timeoffStatusList).then(res => {
        window.location = res.data
        AppStateStore.setProgressSnackbarContent(strings.Exported_Successfully)
    })
}


export function downloadUserRecordsData(userId, startDate, endDate) {
    AppStateStore.setProgressSnackbarContent("loading")
    api.getUserRecordsDataDownload(userId, startDate, endDate).then(res => {
        window.location = res.data
        AppStateStore.setProgressSnackbarContent(strings.Exported_Successfully)
    })
}

export function downloadClientsData(accountId, levelIdFilter, page, limit, search, clientActiveStatus) {
    AppStateStore.setProgressSnackbarContent("loading")
    api.getClientsDataDownload(accountId, levelIdFilter, page, limit, search, clientActiveStatus).then(res => {
        window.location = res.data
        AppStateStore.setProgressSnackbarContent(strings.Exported_Successfully)
    })
}

export function downloadAccountManagerClientAccountsData(clientReportParams: IAccountManagerSearchReportParameters) {
    AppStateStore.setProgressSnackbarContent("loading")
    api.getClientAccountsDataDownload(clientReportParams).then(res => {
        window.location = res.data
        AppStateStore.setProgressSnackbarContent(strings.Exported_Successfully)
    })
}

function handleDownload(result: IReportResult) {
    !result.FullLocked &&
        AppStateStore.setAlertDialogContent({
            title: strings.Warning,
            content: strings.Timecard_Report_Unlock_Warning
        })
    window.location = result.DownloadLink
}

export async function downloadAccrualBalanceReport(accountId, timeoffFilter: ITimeoffFilter) {
    const result = (await api.getAccrualBalanceReport(accountId, timeoffFilter)).data as IReportResult
    window.location = result.DownloadLink
}

export async function downloadNewCustomReport(newCustomReportDocId: string, newCustomReportParameters: INewCustomReportParameters) {
    const asyncSessionId = utils.guid()
    setTimeout(() =>
        dispatcher.dispatch({
            type: "UPDATE_ASYNC_SESSION_ID",
            data: asyncSessionId
        })
        , 0)
    const res = await api.getNewCustomReport(newCustomReportDocId, asyncSessionId, newCustomReportParameters)
    clearAsyncSessionId()
    handleDownload(res.data)
}

export async function downloadEmployeeTimecardReport(accountId: string, userId: string, startDate: string, endDate: string, reportDate: string, includePunchPhoto: boolean) {
    const res = await api.getEmployeeTimecardReport(accountId, userId, startDate, endDate, reportDate, includePunchPhoto)
    window.location = res.data
}

export async function getDashboardWorkingEmployeesDownloadReport(hierarchyItemId, utcTime, filterDirectReportsOnly, filterByPunchPermissionHierarchy) {
    const res = await api.getDashboardWorkingEmployeesDownloadReport(hierarchyItemId, utcTime, filterDirectReportsOnly, filterByPunchPermissionHierarchy)
    window.location = res.data
}

export async function getDashboardWorkingEmployeesAttendanceEmergencyReport(hierarchyItemId, utcTime, filterDirectReportsOnly, filterByPunchPermissionHierarchy) {
    const res = await api.getDashboardWorkingEmployeesAttendanceEmergencyReport(hierarchyItemId, utcTime, filterDirectReportsOnly, filterByPunchPermissionHierarchy)
    window.location = res.data
}

export async function getDashboardOTEmployeesDownloadReport(type: EOTEmployeeType, hierarchyItemId, utcTime, filterDirectReportsOnly, filterByPunchPermissionHierarchy) {
    const res = await api.getDashboardOTEmployeesDownloadReport(type, hierarchyItemId, utcTime, filterDirectReportsOnly, filterByPunchPermissionHierarchy)
    window.location = res.data
}

export async function getExceptionsReport(accountId: string, referenceDate: string, isPayrollPage: boolean, hierarchyItemId: string, filterByDirectReports: boolean, filterByPunchHierarchy: boolean, filterByDefaultHierarchy: boolean, viewPayrollByJob: boolean, filterObj: IPunchExceptionFilterDto) {
    const res = await api.getExceptionsReport(accountId, referenceDate, isPayrollPage, hierarchyItemId, filterByDirectReports, filterByPunchHierarchy, filterByDefaultHierarchy, viewPayrollByJob, filterObj)
    window.location = res.data
}

export async function downloadManualEntriesReport(accountId: string, startDate: string, endDate: string, levelIdFilter: string, payGroupIds: string[], userFilter: IUserFilter, filterByDefaultHierarchy: boolean, filterByDirectReport: boolean, filterByPunchHierarchy: boolean, filterByException: boolean, filterExceptions: EExceptionType[]) {
    const res = await api.getManualEntriesReport(accountId, startDate, endDate, levelIdFilter, payGroupIds, userFilter, filterByDefaultHierarchy, filterByDirectReport, filterByPunchHierarchy, filterByException, filterExceptions)
    window.location = res.data
}

export async function downloadJobTimecardManualEntriesReport(accountId: string, jobId: string, startDate: string, endDate: string, payGroupIds: string[], userFilter: IUserFilter) {
    const res = await api.getJobTimecardManualEntriesReport(accountId, jobId, startDate, endDate, payGroupIds, userFilter)
    window.location = res.data
}